import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Icon from 'antd/lib/icon';

import {
  StyledEditableText,
  StyledEditHint,
  StyledInput,
  StyledTextarea,
  StyledValue,
} from './style';

const EditableText = ({
  name,
  pageId,
  initialText,
  saveChanges,
  createVariables,
  multiline,
  placeholder,
}) => {
  const [text, changeText] = useState(initialText || '');
  const [draft, changeDraft] = useState(initialText || '');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const nextText = initialText || '';

    changeText(nextText);

    if (!isEditing) {
      changeDraft(nextText);
    }
  }, [initialText, isEditing]);

  useEffect(() => {
    if (!isEditing || !inputRef.current) {
      return;
    }

    const input = inputRef.current;
    const end = input.value.length;

    input.focus();
    input.setSelectionRange(end, end);
  }, [isEditing]);

  const stopEditing = () => {
    setIsEditing(false);
  };

  const startEditing = () => {
    setIsEditing(true);
  };

  const cancelEditing = () => {
    changeDraft(text);
    stopEditing();
  };

  const commitChanges = () => {
    const nextValue = draft;

    if (nextValue !== text) {
      changeText(nextValue);
      saveChanges(createVariables(pageId, nextValue, name));
    }

    stopEditing();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEditing();
      return;
    }

    if (!multiline && event.key === 'Enter') {
      event.preventDefault();
      commitChanges();
    }
  };

  const InputComponent = multiline ? StyledTextarea : StyledInput;
  const displayText = text || placeholder;

  return (
    <StyledEditableText $isEditing={isEditing} $multiline={multiline}>
      {isEditing ? (
        <InputComponent
          ref={inputRef}
          onBlur={commitChanges}
          onChange={event => changeDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={multiline ? 1 : undefined}
          value={draft}
        />
      ) : (
        <>
          <StyledValue
            $isPlaceholder={!text}
            onClick={startEditing}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                startEditing();
              }
            }}
          >
            {displayText}
          </StyledValue>
          <StyledEditHint aria-hidden="true">
            <Icon type="edit" />
          </StyledEditHint>
        </>
      )}
    </StyledEditableText>
  );
};

EditableText.propTypes = {
  name: PropTypes.string,
  initialText: PropTypes.string,
  pageId: PropTypes.string.isRequired,
  saveChanges: PropTypes.func.isRequired,
  createVariables: PropTypes.func.isRequired,
  multiline: PropTypes.bool,
  placeholder: PropTypes.string,
};

EditableText.defaultProps = {
  name: undefined,
  initialText: '',
  multiline: false,
  placeholder: '',
};

export default React.memo(EditableText);
